import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { USERS_API_URL } from './config/apiConfig';
import ProtectedRoute from './components/ProtectedRoute';
import PublicRoute from './components/PublicRoute';
import LoginScreen from './components/LoginScreen/LoginScreen';
import ChangePasswordScreen from './components/ChangePasswordScreen/ChangePasswordScreen';
import ForgotPasswordScreen from './components/ForgotPasswordScreen/ForgotPasswordScreen';
import ResetPasswordScreen from './components/ResetPasswordScreen/ResetPasswordScreen';
import SignupScreen from './components/SignupScreen/SignupScreen';
import UserManagementScreen from './components/UserManagementScreen/UserManagementScreen';
import RoleManagementScreen from './components/RoleManagementScreen/RoleManagementScreen';
import ManufacturerScreen from './components/ManufacturerScreen/ManufacturerScreen';
import SupplierScreen from './components/SuppliersScreen/SupplierScreen';
import ComponentCategoryScreen from './components/ComponentCategories/ComponentCategoryScreen';
import ComponentSubcategoryScreen from './components/ComponentSubcategory/ComponentSubcategoryScreen';
import ComponentScreen from './components/ComponentScreen/ComponentScreen';
import AmplifierScreen from './components/AmplifierScreen/AmplifierScreen';
import AmplifierSearchScreen from './components/AmplifierSearchScreen/AmplifierSearchScreen';
import AmplifierDetailsScreen from './components/AmplifierDetailsScreen/AmplifierDetailsScreen';
import TemplateScreen from './components/TemplateScreen/TemplateScreen';
import PublicInfoScreen from './components/PublicInfoScreen/PublicInfoScreen';
import ScreenManagementScreen from './components/ScreenManagementScreen/ScreenManagementScreen';
import HomeScreen from './components/HomeScreen/HomeScreen';
import SiteAdminScreen from './components/SiteAdminScreen/SiteAdminScreen';
import UserProfileScreen from './components/UserProfileScreen/UserProfileScreen';
import DiscussionsScreen from './components/DiscussionsScreen/DiscussionsScreen';
import DiscussionAdminScreen from './components/DiscussionAdminScreen/DiscussionAdminScreen';
import DiscussionTreeAdmin from './components/DiscussionTreeAdmin/DiscussionTreeAdmin';
import AmplifierHierarchyScreen from './components/AmplifierHierarchyScreen/AmplifierHierarchyScreen';
import DuplicateCleanupScreen from './components/DuplicateCleanupScreen/DuplicateCleanupScreen';
import ErrorLogScreen from './components/ErrorLogScreen/ErrorLogScreen';
import ActivityLogScreen from './components/ActivityLogScreen/ActivityLogScreen';
import MyCollectionScreen from './components/MyCollectionScreen/MyCollectionScreen';
import SharedCollectionScreen from './components/SharedCollectionScreen/SharedCollectionScreen';
import CertificateVerifyScreen from './components/CertificateVerifyScreen/CertificateVerifyScreen';
// AccountScreen removed - billing is now integrated into UserProfileScreen
import ScrollToTop from './components/ScrollToTop';
import GoogleAnalytics from './components/GoogleAnalytics';
import NavigationTracker from './components/NavigationTracker';
import FeedbackWidget from './components/FeedbackWidget';
import BetaBanner from './components/BetaBanner';
import FeedbackAdminScreen from './components/FeedbackAdminScreen/FeedbackAdminScreen';
import EmailHealthScreen from './components/EmailHealthScreen/EmailHealthScreen';
import ModerationScreen from './components/ModerationScreen/ModerationScreen';
import ContributorsScreen from './components/ContributorsScreen/ContributorsScreen';
import NotificationBell from './components/NotificationBell/NotificationBell';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import TermsOfServicePage from './components/TermsOfServicePage/TermsOfServicePage';
import PrivacyPolicyPage from './components/PrivacyPolicyPage/PrivacyPolicyPage';
import DmcaPage from './components/DmcaPage/DmcaPage';
import ContactScreen from './components/ContactScreen/ContactScreen';
import LegalFooter from './components/LegalFooter/LegalFooter';
import TosGate from './components/TosGate/TosGate';
import ValuationPortalScreen from './components/ValuationPortalScreen/ValuationPortalScreen';
import ValuationSupportScreen from './components/ValuationSupportScreen/ValuationSupportScreen';
import NewsFeedScreen from './components/NewsFeedScreen/NewsFeedScreen';
import WiringDiagramStudio from './components/WiringDiagramStudio/WiringDiagramStudio';
import VerifyEmailScreen from './components/VerifyEmailScreen/VerifyEmailScreen';
import BottomTabBar from './components/BottomTabBar/BottomTabBar';

const Navigation = ({ isMobileOpen, onCloseMobile }) => {
  const { user, logout, isAdmin, canAccessScreen, isAuthenticated, publicScreens } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    if (onCloseMobile) onCloseMobile();
    await logout(); // Clear auth state first
    navigate('/'); // Then navigate — now isAuthenticated is false so / shows PublicInfoScreen
    // Scroll the main content container to top (the app uses overflow on .main-content, not window)
    const mainContent = document.querySelector('.main-content');
    if (mainContent) mainContent.scrollTop = 0;
  };

  // Define navigation items with their corresponding screen paths
  const navigationItems = [
    { path: '/', label: 'Home', screenPath: '/' },
    { path: '/manufacturers', label: 'Manufacturers', screenPath: '/manufacturers' },
    { path: '/suppliers', label: 'Suppliers', screenPath: '/suppliers' },
    { path: '/component_categories', label: 'Component Categories', screenPath: '/component_categories' },
    { path: '/component_subcategories', label: 'Component Subcategories', screenPath: '/component_subcategories' },
    { path: '/component_screen', label: 'Components', screenPath: '/component_screen' },
    { path: '/amplifier_screen', label: 'Amplifiers', screenPath: '/amplifier_screen' },
    { path: '/amplifier_search', label: 'Search Amplifiers', screenPath: '/amplifier_search' },
    { path: '/templates', label: 'Templates', screenPath: '/templates' },
    { path: '/discussions', label: 'Discussions', screenPath: '/discussions' },
    { path: '/wiring-studio', label: 'Wiring Studio', screenPath: '/wiring-studio', publicAlways: true },
    { path: '/news-feed', label: 'News Feed', screenPath: '/news-feed', publicAlways: true },
    { path: '/contributors', label: 'Contributors', screenPath: '/contributors' }
  ];

  // Admin-only navigation items
  const adminNavigationItems = [
    { path: '/user-management', label: 'User Management', screenPath: '/user-management' },
    { path: '/role-management', label: 'Role Management', screenPath: '/user-management' }, // Both use same screen permission
    { path: '/screen-management', label: 'Screen Management', screenPath: '/user-management' }, // Uses same permission as user management
    { path: '/site-admin', label: 'Site Administration', screenPath: '/user-management' }, // Uses same permission as user management
    { path: '/discussion-admin', label: 'Discussion Admin', screenPath: '/discussion-admin' },
    { path: '/discussion-tree-admin', label: 'Discussion Tree Admin', screenPath: '/discussion-admin' }, // Uses same permission
    { path: '/moderation', label: 'Moderation', screenPath: '/moderation' }, // Admin, Site Mod, Board Mod
    { path: '/amplifier-hierarchy', label: 'Amp Hierarchy', screenPath: '/user-management' }, // Admin only
    { path: '/duplicate-cleanup', label: 'Duplicate Cleanup', screenPath: '/user-management' }, // Admin only
    { path: '/error-log', label: 'Error Log', screenPath: '/user-management' }, // Admin only
    { path: '/activity-log', label: 'Activity Log', screenPath: '/user-management' }, // Admin only
    { path: '/feedback-admin', label: 'Feedback Admin', screenPath: '/feedback-admin' },
    { path: '/valuation-support', label: 'Valuation Support', screenPath: '/valuation-support' },
    { path: '/email-health', label: 'Email Health', screenPath: '/user-management' } // Admin only
  ];

  // Handle link click on mobile - close the menu
  const handleLinkClick = () => {
    if (onCloseMobile) onCloseMobile();
  };

  return (
    <nav 
      className={isMobileOpen ? 'mobile-nav-open' : 'desktop-nav'}
      style={{
        width: isMobileOpen ? '280px' : '280px',
        maxWidth: isMobileOpen ? '85vw' : '280px',
        background: '#1a202c',
        padding: '1.5rem',
        borderRight: 'none',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '4px 0 20px rgba(0, 0, 0, 0.1)',
        position: isMobileOpen ? 'fixed' : 'relative',
        overflow: 'hidden',
        height: '100vh',
        zIndex: isMobileOpen ? 1001 : 'auto'
      }}>
      {/* Close button for mobile */}
      {isMobileOpen && (
        <button
          onClick={onCloseMobile}
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            background: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: '50%',
            width: '36px',
            height: '36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 10,
            color: 'white',
            fontSize: '20px'
          }}
        >
          ✕
        </button>
      )}
      
      {/* Background decoration */}
      <div style={{
        position: 'absolute',
        top: '-50%',
        right: '-20%',
        width: '60%',
        height: '200%',
        background: 'rgba(255, 255, 255, 0.03)',
        borderRadius: '50%',
        transform: 'rotate(15deg)'
      }} />
      
      {/* Logo/Brand Section */}
      <div style={{ 
        marginBottom: '2rem',
        textAlign: 'center',
        position: 'relative',
        zIndex: 1,
        flexShrink: 0
      }}>
        <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
          <img 
            src="/AmpNebulaSmall2.jpg" 
            alt="AmpNebula" 
            style={{
              width: '100%',
              height: 'auto',
              borderRadius: '8px'
            }}
          />
          <span style={{
            position: 'absolute',
            top: '27%',
            right: '1%',
            fontSize: '0.5rem',
            color: 'rgba(255,255,255,0.8)',
            fontWeight: '600',
            textShadow: '0 1px 2px rgba(0,0,0,0.5)',
            lineHeight: 1
          }}>™</span>
        </div>
      </div>

      {/* User info - only show if authenticated */}
      {isAuthenticated && (
        <div style={{ 
          marginBottom: '2rem', 
          paddingBottom: '1rem', 
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '12px',
          padding: '1rem',
          position: 'relative',
          zIndex: 3,
          flexShrink: 0
        }}>
          {/* User Photo - Clickable to go to profile */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <Link to="/profile" onClick={handleLinkClick} style={{ textDecoration: 'none' }}>
              {user?.has_photo ? (
                <>
                <img 
                  src={`${USERS_API_URL}/users/${user.user_id}/photo`}
                  alt={user.username}
                  style={{ 
                    width: 48, 
                    height: 48, 
                    borderRadius: '50%', 
                    objectFit: 'cover',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    if (e.target.nextElementSibling) e.target.nextElementSibling.style.display = 'flex';
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.border = '2px solid rgba(255, 255, 255, 0.6)';
                    e.target.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.border = '2px solid rgba(255, 255, 255, 0.3)';
                    e.target.style.transform = 'scale(1)';
                  }}
                />
                {/* Hidden fallback — revealed by onError if photo fails to load */}
                <div style={{ 
                  width: 48, height: 48, borderRadius: '50%', background: '#2563eb',
                  display: 'none', alignItems: 'center', justifyContent: 'center',
                  fontSize: '20px', fontWeight: 'bold', color: 'white',
                  border: '2px solid rgba(255, 255, 255, 0.3)', cursor: 'pointer'
                }}>
                  {user?.username?.charAt(0).toUpperCase() || '?'}
                </div>
                </>
              ) : (
                <div style={{ 
                  width: 48, 
                  height: 48, 
                  borderRadius: '50%', 
                  background: '#2563eb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                  fontWeight: 'bold',
                  color: 'white',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.border = '2px solid rgba(255, 255, 255, 0.6)';
                  e.target.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.border = '2px solid rgba(255, 255, 255, 0.3)';
                  e.target.style.transform = 'scale(1)';
                }}>
                {user?.username?.charAt(0).toUpperCase() || '?'}
              </div>
            )}
            </Link>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                Welcome back,
              </div>
              <div style={{ fontSize: '1rem', color: 'white', fontWeight: '600' }}>
                {user?.nickname || user?.username}
              </div>
            </div>
            <NotificationBell />
          </div>
          {isAdmin && (
            <div style={{ 
              fontSize: '0.75rem', 
              color: '#4fd1c7', 
              fontWeight: 'bold',
              background: 'rgba(79, 209, 199, 0.1)',
              padding: '0.25rem 0.5rem',
              borderRadius: '6px',
              display: 'inline-block',
              marginBottom: '0.75rem'
            }}>
              Administrator
            </div>
          )}
          <button 
            onClick={handleLogout}
            style={{
              width: '100%',
              marginTop: '0.5rem',
              padding: '0.5rem 1rem',
              background: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: '600',
              transition: 'all 0.2s ease',
              boxShadow: 'none'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 6px 16px rgba(253, 121, 168, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(253, 121, 168, 0.3)';
            }}
          >
            Logout
          </button>
        </div>
      )}

      {/* Login prompt for unauthenticated users */}
      {!isAuthenticated && (
        <div style={{ 
          marginBottom: '2rem', 
          paddingBottom: '1rem', 
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '12px',
          padding: '1rem',
          position: 'relative',
          zIndex: 1,
          flexShrink: 0
        }}>
          <div style={{ 
            fontSize: '0.9rem', 
            color: 'rgba(255, 255, 255, 0.9)', 
            marginBottom: '0.75rem',
            fontWeight: '500'
          }}>
            Welcome to AmpNebula
          </div>
          <Link 
            to="/login"
            onClick={handleLinkClick}
            style={{
              display: 'block',
              width: '100%',
              padding: '0.75rem 1rem',
              background: '#2563eb',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: '600',
              textAlign: 'center',
              transition: 'all 0.2s ease',
              boxShadow: 'none',
              boxSizing: 'border-box'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 6px 16px rgba(116, 185, 255, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(116, 185, 255, 0.3)';
            }}
          >
            Login / Sign up
          </Link>
        </div>
      )}

      {/* Navigation links */}
      <div style={{ flex: 1, position: 'relative', zIndex: 1, overflowY: 'auto', overflowX: 'hidden', paddingBottom: '150px' }}>
        <div style={{
          fontSize: '0.75rem',
          color: 'rgba(255, 255, 255, 0.5)',
          fontWeight: '600',
          letterSpacing: '1px',
          marginBottom: '1rem',
          textTransform: 'uppercase'
        }}>
          Navigation
        </div>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {/* Render navigation items based on permissions */}
          {navigationItems.map(item => {
            // Always show public screens, or show private screens if user has access
            const isPublic = publicScreens.some(screen => screen.screen_path === item.screenPath);
            const hasAccess = canAccessScreen(item.screenPath);
            
            const isActive = location.pathname === item.path;
            
            return (item.publicAlways || isPublic || hasAccess) && (
              <li key={item.path} style={{ marginBottom: '0.5rem' }}>
                <Link 
                  to={item.path}
                  onClick={handleLinkClick}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.75rem 1rem',
                    color: isActive ? 'white' : 'rgba(255, 255, 255, 0.8)',
                    textDecoration: 'none',
                    borderRadius: '10px',
                    fontSize: '0.9rem',
                    fontWeight: isActive ? '600' : '500',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    backgroundColor: isActive ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                    boxShadow: isActive ? '0 2px 8px rgba(0, 0, 0, 0.1)' : 'none',
                    borderLeft: isActive ? `3px solid ${isPublic && isAdmin ? '#4fd1c7' : '#74b9ff'}` : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                      e.target.style.color = 'white';
                      e.target.style.transform = 'translateX(4px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = 'rgba(255, 255, 255, 0.8)';
                      e.target.style.transform = 'translateX(0)';
                    }
                  }}
                >
                  <span style={{
                    width: isActive ? '8px' : '6px',
                    height: isActive ? '8px' : '6px',
                    backgroundColor: isPublic && isAdmin ? '#4fd1c7' : '#74b9ff',
                    borderRadius: '50%',
                    marginRight: '0.75rem',
                    flexShrink: 0,
                    boxShadow: isActive ? `0 0 8px ${isPublic && isAdmin ? '#4fd1c7' : '#74b9ff'}` : 'none',
                    transition: 'all 0.2s ease'
                  }} />
                  {item.label}
                  {isPublic && isAdmin && (
                    <span style={{
                      fontSize: '0.7rem',
                      marginLeft: 'auto',
                      color: 'rgba(79, 209, 199, 0.8)',
                      fontWeight: '600'
                    }}>
                      PUBLIC
                    </span>
                  )}
                </Link>
              </li>
            );
          })}

        {/* Value an Amp — public tool, visible to all users */}
        <li style={{ marginBottom: '0.5rem' }}>
          <Link
            to="/value"
            onClick={handleLinkClick}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '0.75rem 1rem',
              borderRadius: '10px',
              textDecoration: 'none',
              color: location.pathname.startsWith('/value') ? 'white' : 'rgba(255, 255, 255, 0.8)',
              fontSize: '0.9rem',
              fontWeight: location.pathname.startsWith('/value') ? '600' : '500',
              transition: 'all 0.2s ease',
              backgroundColor: location.pathname.startsWith('/value') ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
              boxShadow: location.pathname.startsWith('/value') ? '0 2px 8px rgba(0, 0, 0, 0.1)' : 'none',
              borderLeft: location.pathname.startsWith('/value') ? '3px solid #74b9ff' : 'none'
            }}
            onMouseEnter={(e) => {
              if (!location.pathname.startsWith('/value')) {
                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                e.target.style.color = 'white';
                e.target.style.transform = 'translateX(4px)';
              }
            }}
            onMouseLeave={(e) => {
              if (!location.pathname.startsWith('/value')) {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = 'rgba(255, 255, 255, 0.8)';
                e.target.style.transform = 'translateX(0)';
              }
            }}
          >
            <span style={{
              width: location.pathname.startsWith('/value') ? '8px' : '6px',
              height: location.pathname.startsWith('/value') ? '8px' : '6px',
              backgroundColor: '#74b9ff',
              borderRadius: '50%',
              marginRight: '0.75rem',
              flexShrink: 0,
              boxShadow: location.pathname.startsWith('/value') ? '0 0 8px #74b9ff' : 'none',
              transition: 'all 0.2s ease'
            }} />
            Value an Amp
          </Link>
        </li>

        {/* My Collection - authenticated users only */}
        {isAuthenticated && (
          <li style={{ marginBottom: '0.5rem' }}>
            <Link 
              to="/my-collection"
              onClick={handleLinkClick}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '0.75rem 1rem',
                borderRadius: '10px',
                textDecoration: 'none',
                color: location.pathname === '/my-collection' ? 'white' : 'rgba(255, 255, 255, 0.8)',
                fontSize: '0.9rem',
                fontWeight: location.pathname === '/my-collection' ? '600' : '500',
                transition: 'all 0.2s ease',
                backgroundColor: location.pathname === '/my-collection' ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                boxShadow: location.pathname === '/my-collection' ? '0 2px 8px rgba(0, 0, 0, 0.1)' : 'none',
                borderLeft: location.pathname === '/my-collection' ? '3px solid #3fb950' : 'none'
              }}
              onMouseEnter={(e) => {
                if (location.pathname !== '/my-collection') {
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.color = 'white';
                  e.target.style.transform = 'translateX(4px)';
                }
              }}
              onMouseLeave={(e) => {
                if (location.pathname !== '/my-collection') {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = 'rgba(255, 255, 255, 0.8)';
                  e.target.style.transform = 'translateX(0)';
                }
              }}
            >
              <span style={{
                width: location.pathname === '/my-collection' ? '8px' : '6px',
                height: location.pathname === '/my-collection' ? '8px' : '6px',
                backgroundColor: '#3fb950',
                borderRadius: '50%',
                marginRight: '0.75rem',
                flexShrink: 0,
                boxShadow: location.pathname === '/my-collection' ? '0 0 8px #3fb950' : 'none',
                transition: 'all 0.2s ease'
              }} />
              My Collection
            </Link>
          </li>
        )}

        {/* About AmpNebula — always last in the main nav, visible to all users */}
        {(() => {
          const aboutIsPublic = publicScreens.some(s => s.screen_path === '/public-info');
          const aboutHasAccess = canAccessScreen('/public-info');
          const aboutIsActive = location.pathname === '/public-info';
          return (aboutIsPublic || aboutHasAccess) ? (
            <li style={{ marginBottom: '0.5rem' }}>
              <Link
                to="/public-info"
                onClick={handleLinkClick}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.75rem 1rem',
                  color: aboutIsActive ? 'white' : 'rgba(255, 255, 255, 0.8)',
                  textDecoration: 'none',
                  borderRadius: '10px',
                  fontSize: '0.9rem',
                  fontWeight: aboutIsActive ? '600' : '500',
                  transition: 'all 0.2s ease',
                  backgroundColor: aboutIsActive ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                  boxShadow: aboutIsActive ? '0 2px 8px rgba(0, 0, 0, 0.1)' : 'none',
                  borderLeft: aboutIsActive ? `3px solid ${aboutIsPublic && isAdmin ? '#4fd1c7' : '#74b9ff'}` : 'none'
                }}
                onMouseEnter={(e) => {
                  if (!aboutIsActive) {
                    e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                    e.target.style.color = 'white';
                    e.target.style.transform = 'translateX(4px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!aboutIsActive) {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = 'rgba(255, 255, 255, 0.8)';
                    e.target.style.transform = 'translateX(0)';
                  }
                }}
              >
                <span style={{
                  width: aboutIsActive ? '8px' : '6px',
                  height: aboutIsActive ? '8px' : '6px',
                  backgroundColor: aboutIsPublic && isAdmin ? '#4fd1c7' : '#74b9ff',
                  borderRadius: '50%',
                  marginRight: '0.75rem',
                  flexShrink: 0,
                  boxShadow: aboutIsActive ? `0 0 8px ${aboutIsPublic && isAdmin ? '#4fd1c7' : '#74b9ff'}` : 'none',
                  transition: 'all 0.2s ease'
                }} />
                About AmpNebula
                {aboutIsPublic && isAdmin && (
                  <span style={{
                    fontSize: '0.7rem',
                    marginLeft: 'auto',
                    color: 'rgba(79, 209, 199, 0.8)',
                    fontWeight: '600'
                  }}>
                    PUBLIC
                  </span>
                )}
              </Link>
            </li>
          ) : null;
        })()}

        {/* Admin-only navigation items */}
        {adminNavigationItems.map(item => {
          const isAdminActive = location.pathname === item.path;
          
          return canAccessScreen(item.screenPath) && (
            <li key={item.path} style={{ marginBottom: '0.5rem' }}>
              <Link 
                to={item.path}
                onClick={handleLinkClick}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.75rem 1rem',
                  borderRadius: '12px',
                  textDecoration: 'none',
                  color: isAdminActive ? 'white' : 'rgba(255, 255, 255, 0.8)',
                  fontSize: '0.9rem',
                  fontWeight: isAdminActive ? '600' : '500',
                  transition: 'all 0.3s ease',
                  backgroundColor: isAdminActive ? 'rgba(255, 46, 84, 0.15)' : 'transparent',
                  border: `1px solid ${isAdminActive ? 'rgba(255, 46, 84, 0.5)' : 'rgba(255, 46, 84, 0.3)'}`,
                  boxShadow: isAdminActive ? '0 2px 8px rgba(0, 0, 0, 0.1)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (!isAdminActive) {
                    e.target.style.backgroundColor = 'rgba(255, 46, 84, 0.1)';
                    e.target.style.color = 'white';
                    e.target.style.transform = 'translateX(4px)';
                    e.target.style.borderColor = 'rgba(255, 46, 84, 0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isAdminActive) {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = 'rgba(255, 255, 255, 0.8)';
                    e.target.style.transform = 'translateX(0)';
                    e.target.style.borderColor = 'rgba(255, 46, 84, 0.3)';
                  }
                }}
              >
                <span style={{
                  width: isAdminActive ? '8px' : '6px',
                  height: isAdminActive ? '8px' : '6px',
                  backgroundColor: '#ff2e54',
                  borderRadius: '50%',
                  marginRight: '0.75rem',
                  flexShrink: 0,
                  boxShadow: isAdminActive ? '0 0 8px #ff2e54' : 'none',
                  transition: 'all 0.2s ease'
                }} />
                {item.label}
                <span style={{
                  fontSize: '0.7rem',
                  marginLeft: 'auto',
                  color: 'rgba(255, 46, 84, 0.8)',
                  fontWeight: '600'
                }}>
                  ADMIN
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
      </div>
    </nav>
  );
};

const AppContent = () => {
  const { isAuthenticated, loading, sessionExpired, clearSessionExpired } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);
  // Bottom tab bar auto-hide: hide while scrolling down, reappear when scrolling up
  const [tabBarVisible, setTabBarVisible] = React.useState(true);

  React.useEffect(() => {
    const mainContent = document.querySelector('.main-content');
    if (!mainContent) return;
    let lastScrollY = mainContent.scrollTop;
    const onScroll = () => {
      const current = mainContent.scrollTop;
      setTabBarVisible(current <= lastScrollY || current < 80);
      lastScrollY = current;
    };
    mainContent.addEventListener('scroll', onScroll, { passive: true });
    return () => mainContent.removeEventListener('scroll', onScroll);
  }, []);

  // Hide sidebar nav on showcase/valuation pages for an immersive full-width experience
  const isShowcasePage = location.pathname.startsWith('/collection/')
    || location.pathname.startsWith('/value');

  const handleSessionExpiredOk = () => {
    clearSessionExpired();
    navigate('/login');
  };

  // Show loading screen while authentication is being verified
  // Exception: amp detail pages are publicly accessible — skip the loading gate so they
  // render immediately on first load instead of showing a blank screen while auth settles.
  const isAmpPage = location.pathname.startsWith('/amps/') || location.pathname.startsWith('/amplifier-components/');
  if (loading && !isAmpPage) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        fontSize: '1.2rem',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  // Wrapper component to allow unauthenticated access to change-password when coming from login
  const ChangePasswordRoute = () => {
    const location = useLocation();
    const fromLogin = location.state?.fromLogin;
    
    if (fromLogin) {
      // Allow unauthenticated access for expired password flow
      return <ChangePasswordScreen />;
    }
    
    // Otherwise require authentication
    return <ProtectedRoute><ChangePasswordScreen /></ProtectedRoute>;
  };

  // Always show the navigation, but handle authentication in individual routes
  return (
    <>
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {/* Mobile header bar — hidden on showcase pages */}
      {!isShowcasePage && (
        <div className="mobile-header">
          <button 
            className="hamburger-btn"
            onClick={() => setMobileNavOpen(true)}
            aria-label="Open menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
          <div style={{
            fontSize: '1.2rem',
            fontWeight: '700',
            color: '#2563eb'
          }}>
            AmpNebula<sup style={{ fontSize: '0.5em', verticalAlign: 'super' }}>™</sup>
          </div>
          {/* Bell hidden on mobile — the bottom tab bar carries it there.
               Visible here only on desktop where no bottom bar exists. */}
          <div className="mobile-header-bell"
               style={{ width: '40px', display: 'flex', justifyContent: 'center' }}>
            {isAuthenticated && <NotificationBell />}
          </div>
        </div>
      )}
      
      {/* Mobile overlay — hidden on showcase pages */}
      {!isShowcasePage && mobileNavOpen && (
        <div 
          className="mobile-overlay"
          onClick={() => setMobileNavOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1000
          }}
        />
      )}
      
      {/* Navigation sidebar — hidden on showcase pages for immersive experience */}
      {!isShowcasePage && (
        <Navigation 
          isMobileOpen={mobileNavOpen} 
          onCloseMobile={() => setMobileNavOpen(false)} 
        />
      )}
      <main className="main-content main-content-mobile" style={{ flex: 1, padding: '0', overflowY: 'auto', background: isShowcasePage ? 'transparent' : '#f8f9fa' }}>
        <Routes>
          {/* Authentication routes - always public */}
          <Route path="/login" element={<PublicRoute><LoginScreen /></PublicRoute>} />
          <Route path="/signup" element={<PublicRoute><SignupScreen /></PublicRoute>} />
          <Route path="/verify-email" element={<VerifyEmailScreen />} />
          <Route path="/verify-email/:token" element={<VerifyEmailScreen />} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordScreen /></PublicRoute>} />
          <Route path="/reset-password" element={<PublicRoute><ResetPasswordScreen /></PublicRoute>} />
          
          {/* Change password - conditional authentication based on fromLogin state */}
          <Route path="/change-password" element={<ChangePasswordRoute />} />
          
          {/* User Profile - requires authentication */}
          <Route path="/profile" element={
            <ProtectedRoute>
              <UserProfileScreen />
            </ProtectedRoute>
          } />
          
          {/* Account & Billing - redirect to profile (billing is now in profile) */}
          <Route path="/account" element={<Navigate to="/profile" replace />} />
          
          {/* My Collection - requires authentication */}
          <Route path="/my-collection" element={
            <ProtectedRoute>
              <MyCollectionScreen />
            </ProtectedRoute>
          } />
          
          {/* Admin routes */}
          <Route path="/user-management" element={
            <ProtectedRoute requireAdmin={true} screenPath="/user-management">
              <UserManagementScreen />
            </ProtectedRoute>
          } />
          <Route path="/role-management" element={
            <ProtectedRoute requireAdmin={true} screenPath="/user-management">
              <RoleManagementScreen />
            </ProtectedRoute>
          } />
          <Route path="/screen-management" element={
            <ProtectedRoute requireAdmin={true} screenPath="/user-management">
              <ScreenManagementScreen />
            </ProtectedRoute>
          } />
          <Route path="/site-admin" element={
            <ProtectedRoute screenPath="/site-admin">
              <SiteAdminScreen />
            </ProtectedRoute>
          } />
          <Route path="/amplifier-hierarchy" element={
            <ProtectedRoute requireAdmin={true} screenPath="/user-management">
              <AmplifierHierarchyScreen />
            </ProtectedRoute>
          } />
          <Route path="/duplicate-cleanup" element={
            <ProtectedRoute requireAdmin={true} screenPath="/user-management">
              <DuplicateCleanupScreen />
            </ProtectedRoute>
          } />
          <Route path="/error-log" element={
            <ProtectedRoute requireAdmin={true} screenPath="/user-management">
              <ErrorLogScreen />
            </ProtectedRoute>
          } />
          <Route path="/activity-log" element={
            <ProtectedRoute requireAdmin={true} screenPath="/user-management">
              <ActivityLogScreen />
            </ProtectedRoute>
          } />
          <Route path="/feedback-admin" element={
            <ProtectedRoute screenPath="/feedback-admin">
              <FeedbackAdminScreen />
            </ProtectedRoute>
          } />
          <Route path="/valuation-support" element={
            <ProtectedRoute screenPath="/valuation-support">
              <ValuationSupportScreen />
            </ProtectedRoute>
          } />
          <Route path="/email-health" element={
            <ProtectedRoute requireAdmin={true} screenPath="/user-management">
              <EmailHealthScreen />
            </ProtectedRoute>
          } />
          <Route path="/moderation" element={
            <ProtectedRoute screenPath="/moderation">
              <ModerationScreen />
            </ProtectedRoute>
          } />
          
          {/* Main application routes - can be public or protected depending on screen configuration */}
          <Route path="/" element={isAuthenticated ? <HomeScreen /> : <PublicInfoScreen />} />
          
          {/* About AmpNebula — also accessible at /public-info for direct links */}
          <Route path="/public-info" element={
            <ProtectedRoute screenPath="/public-info">
              <PublicInfoScreen />
            </ProtectedRoute>
          } />
          <Route path="/manufacturers" element={
            <ProtectedRoute screenPath="/manufacturers">
              <ManufacturerScreen />
            </ProtectedRoute>
          } />
          <Route path="/suppliers" element={
            <ProtectedRoute screenPath="/suppliers">
              <SupplierScreen />
            </ProtectedRoute>
          } />
          <Route path="/component_categories" element={
            <ProtectedRoute screenPath="/component_categories">
              <ComponentCategoryScreen />
            </ProtectedRoute>
          } />
          <Route path="/component_subcategories" element={
            <ProtectedRoute screenPath="/component_subcategories">
              <ComponentSubcategoryScreen />
            </ProtectedRoute>
          } />
          <Route path="/component_screen" element={
            <ProtectedRoute screenPath="/component_screen">
              <ComponentScreen />
            </ProtectedRoute>
          } />
          <Route path="/amplifier_screen" element={
            <ProtectedRoute screenPath="/amplifier_screen">
              <AmplifierScreen />
            </ProtectedRoute>
          } />
          <Route path="/amplifier_search" element={
            <ProtectedRoute screenPath="/amplifier_search">
              <AmplifierSearchScreen />
            </ProtectedRoute>
          } />
          <Route path="/amplifier_components_screen" element={
            <ProtectedRoute screenPath="/amplifier-components/:amplifierId">
              <AmplifierDetailsScreen />
            </ProtectedRoute>
          } />
          <Route path="/amplifier-components/:amplifierId" element={
            <ProtectedRoute screenPath="/amplifier-components/:amplifierId">
              <AmplifierDetailsScreen />
            </ProtectedRoute>
          } />
          <Route path="/amps/:manufacturerSlug/:ampSlug" element={
            <ProtectedRoute screenPath="/amplifier-components/:amplifierId">
              <AmplifierDetailsScreen />
            </ProtectedRoute>
          } />
          <Route path="/templates" element={
            <ProtectedRoute screenPath="/templates">
              <TemplateScreen />
            </ProtectedRoute>
          } />
          <Route path="/discussions" element={
            <ProtectedRoute screenPath="/discussions">
              <DiscussionsScreen />
            </ProtectedRoute>
          } />
          <Route path="/discussions/topic/:topicId" element={
            <ProtectedRoute screenPath="/discussions">
              <DiscussionsScreen />
            </ProtectedRoute>
          } />
          <Route path="/discussion-admin" element={
            <ProtectedRoute screenPath="/discussion-admin" requireAdmin={true}>
              <DiscussionAdminScreen />
            </ProtectedRoute>
          } />
          <Route path="/discussion-tree-admin" element={
            <ProtectedRoute screenPath="/discussion-admin" requireAdmin={true}>
              <DiscussionTreeAdmin />
            </ProtectedRoute>
          } />
          
          {/* Public valuation portal — no auth required */}
          <Route path="/value" element={<ValuationPortalScreen />} />

          {/* News Feed — always public */}
          <Route path="/news-feed" element={<NewsFeedScreen />} />

          {/* Public shared collection page — always read-only */}
          <Route path="/collection/:shareToken" element={<SharedCollectionScreen />} />
          {/* Owner edit mode — requires auth, same component with editRoute flag */}
          <Route path="/collection/:shareToken/edit" element={<SharedCollectionScreen editRoute />} />

          {/* Public certificate verification — QR code / link from PDF */}
          <Route path="/verify/:token" element={<CertificateVerifyScreen />} />

          {/* Legal pages — always public */}
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/dmca" element={<DmcaPage />} />
          <Route path="/contact" element={<ContactScreen />} />

          {/* Wiring Diagram Studio */}
          <Route path="/wiring-studio" element={
            <ProtectedRoute screenPath="/wiring-studio" isPublic>
              <WiringDiagramStudio />
            </ProtectedRoute>
          } />
          <Route path="/wiring-studio/:id" element={
            <ProtectedRoute screenPath="/wiring-studio" isPublic>
              <WiringDiagramStudio />
            </ProtectedRoute>
          } />

          {/* Contributors leaderboard — public */}
          <Route path="/contributors" element={
            <ProtectedRoute screenPath="/contributors">
              <ContributorsScreen />
            </ProtectedRoute>
          } />

          {/* Fallback route */}
          <Route path="*" element={
            isAuthenticated ? (
              <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                <h2>Page not found</h2>
                <Link to="/">Return to Home</Link>
              </div>
            ) : (
              <PublicRoute><LoginScreen /></PublicRoute>
            )
          } />
        </Routes>
        {/* Legal footer — hidden on auth & showcase pages */}
        <LegalFooter />
      </main>
    </div>

    {/* Bottom tab bar — mobile/tablet only (≤1024px), hidden on showcase pages */}
    {!isShowcasePage && (
      <BottomTabBar
        onOpenMenu={() => setMobileNavOpen(true)}
        isAuthenticated={isAuthenticated}
        isVisible={tabBarVisible}
      />
    )}

    {/* Session-expired modal */}
    {sessionExpired && (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 99999,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{
          background: '#fff', borderRadius: 10, padding: '2rem 2.5rem',
          boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
          textAlign: 'center', maxWidth: 360, width: '90%',
        }}>
          <div style={{ fontSize: 40, marginBottom: '0.5rem' }}>⏱</div>
          <h2 style={{ margin: '0 0 0.5rem', color: '#1a202c', fontSize: '1.25rem' }}>Session Timed Out</h2>
          <p style={{ margin: '0 0 1.5rem', color: '#4a5568', fontSize: '0.95rem' }}>
            Your session has expired. Please log in again to continue.
          </p>
          <button
            onClick={handleSessionExpiredOk}
            style={{
              background: '#2563eb', color: '#fff', border: 'none',
              borderRadius: 6, padding: '0.6rem 2rem',
              fontSize: '1rem', fontWeight: 600, cursor: 'pointer',
            }}
          >
            OK
          </button>
        </div>
      </div>
    )}
    </>
  );
};

const App = () => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <GoogleAnalytics />
          <NavigationTracker />
          <FeedbackWidget />
          <BetaBanner />
          <TosGate />
          <AppContent />
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;
